'use client';
import { PlayerItem } from '@/modules/league/new-components/rsvp-components/PlayersBoxComponent/PlayerItem';
import { PlayerItemPartner } from '@/modules/league/new-components/rsvp-components/PlayersBoxComponent/PlayerItemPartner';
import { GameDayInfo, Player } from '@/modules/league/new-components/types';

interface PlayerListProps {
  players: Player[];
  headerContent: GameDayInfo;
  userUuid?: string;
  cardName: string;
  isAuthorized: boolean | null;
  playersForOptIn: Player[];
  playersForOptOut: Player[];
  playersForRemove: Player[];
  playersForWait: Player[];
  playersForCheckIn: number;
  toggleOptIn: (player: Player) => Promise<void>;
  toggleOptOut: (player: Player) => Promise<void>;
  toggleRemove: (player: Player) => Promise<void>;
  toggleWait: (player: Player) => Promise<void>;
  canMoveToWait: boolean;
  canOptIn: boolean;
  isOptInButtonDisabled: boolean;
  setPlayersForCheckIn: React.Dispatch<React.SetStateAction<number>>;
  setUncheckedPlayers: React.Dispatch<React.SetStateAction<Player[]>>;
  uncheckedPlayers: Player[];
  chargeOptIn: boolean;
  chargeOptInURL: string;
  slug: string;
}

export const CheckInSection = ({
  cardName,
  isAuthorized,
  playingCounter,
  totalPlayersInCard,
  toggleOptIn,
  toggleOptOut,
  toggleRemove,
  toggleWait,
  playersForCheckIn,
  players,
  maxPlayingCount,
  isLocked,
  isPartnerLeague,
  showOnlyUncheckedPlayers,
  setShowOnlyUncheckedPlayers,
}: {
  cardName: string;
  isAuthorized: boolean | null;
  playingCounter: number;
  totalPlayersInCard: number;
  toggleOptOut: (player: Player) => Promise<void>;
  toggleRemove: (player: Player) => Promise<void>;
  toggleWait: (player: Player) => Promise<void>;
  toggleOptIn: (player: Player) => Promise<void>;
  playersForCheckIn: number;
  players: Player[];
  maxPlayingCount: number;
  isLocked: boolean;
  isPartnerLeague: boolean;
  showOnlyUncheckedPlayers: boolean;
  setShowOnlyUncheckedPlayers: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  // select all handler logic (unchanged)
  const handleSelectAll = () => {
    if (cardName === 'Playing') {
      // Toggle players out (opt-out) for 'Playing' card
      players.forEach((player) => toggleOptOut(player));
    } else if (cardName === 'Not Playing') {
      if (maxPlayingCount === -1) {
        // Toggle all players in (opt-in) if there's no limit
        players.forEach((player) => toggleOptIn(player));
      } else if (maxPlayingCount === playingCounter) {
        players.forEach((player) => toggleWait(player));
      } else {
        // Only toggle in the number of players allowed based on maxPlayingCount
        const availableSlots = maxPlayingCount - playingCounter;
        const playersToOptIn = players.slice(0, availableSlots);
        playersToOptIn.forEach((player) => toggleOptIn(player));
      }
    } else if (cardName === 'Waitlist') {
      // Toggle remove all players for 'Remove' card
      players.forEach((player) => toggleRemove(player));
    }
  };

  // Handle Showing un-checked palyers
  const handleUncheckedPlayers = () => {
    if (!showOnlyUncheckedPlayers) {
      setShowOnlyUncheckedPlayers(true);
    } else {
      setShowOnlyUncheckedPlayers(false);
    }
  };

  const allPlayersHavePartners =
    cardName === 'Not Playing' &&
    isPartnerLeague &&
    players.every((player) => player.PlayerFullName_2 !== '' && player.UserID_2 !== null);
  const shouldShowSelectAll =
    isAuthorized &&
    totalPlayersInCard !== 0 &&
    !isLocked &&
    (allPlayersHavePartners || !isPartnerLeague) &&
    (maxPlayingCount === -1 || maxPlayingCount - playingCounter >= playersForCheckIn);

  return (
    <div className={`flex px-4 pt-2 ${cardName === 'Playing' ? 'justify-between' : 'justify-end'}`}>
      {cardName === 'Playing' && playingCounter > 0 && (
        <div
          className='flex cursor-pointer gap-1 text-xs font-medium text-leagues-300 hover:text-leagues-300/70'
          onClick={handleUncheckedPlayers}
        >
          Checked-in ({playersForCheckIn}/{players.length})
        </div>
      )}
      {shouldShowSelectAll ? (
        <p
          className='cursor-pointer text-xs font-medium text-leagues-300 hover:text-leagues-300/70'
          onClick={handleSelectAll}
        >
          Select all
        </p>
      ) : null}
    </div>
  );
};

export const PlayerList = ({
  players,
  headerContent,
  userUuid,
  cardName,
  isAuthorized,
  playersForOptIn,
  playersForOptOut,
  playersForRemove,
  playersForWait,
  playersForCheckIn,
  setPlayersForCheckIn,
  toggleOptIn,
  toggleOptOut,
  toggleRemove,
  toggleWait,
  canMoveToWait,
  canOptIn,
  isOptInButtonDisabled,
  setUncheckedPlayers,
  uncheckedPlayers,
  chargeOptIn,
  chargeOptInURL,
  slug,
}: PlayerListProps) => (
  <div className='flex flex-col gap-0 rounded-t-lg'>
    {players.map((player, index) =>
      headerContent.details.playerTypeTitle === 'Partner' ||
      headerContent.details.playerTypeId === 2 ? (
        <PlayerItemPartner
          key={index}
          player={player}
          headerContent={headerContent}
          userUuid={userUuid || ''}
          cardName={cardName}
          index={index}
          isAuthorized={isAuthorized}
          playersForOptIn={playersForOptIn}
          toggleOptIn={toggleOptIn}
          toggleOptOut={toggleOptOut}
          toggleRemove={toggleRemove}
          toggleWait={toggleWait}
          canMoveToWait={canMoveToWait}
          canOptIn={canOptIn}
          isOptInButtonDisabled={isOptInButtonDisabled}
          playersForOptOut={playersForOptOut}
          playersForRemove={playersForRemove}
          playersForWait={playersForWait}
          playersForCheckIn={playersForCheckIn}
          setPlayersForCheckIn={setPlayersForCheckIn}
          duprPromptEnabled={headerContent.details.duprPromptEnabled}
          numberOfPlayers={players.length}
          chargeOptIn={chargeOptIn}
          chargeOptInURL={chargeOptInURL}
          slug={slug}
        />
      ) : (
        <PlayerItem
          key={index}
          player={player}
          headerContent={headerContent}
          userUuid={userUuid || ''}
          cardName={cardName}
          index={index}
          isAuthorized={isAuthorized}
          playersForOptIn={playersForOptIn}
          toggleOptIn={toggleOptIn}
          toggleOptOut={toggleOptOut}
          toggleRemove={toggleRemove}
          toggleWait={toggleWait}
          canMoveToWait={canMoveToWait}
          canOptIn={canOptIn}
          isOptInButtonDisabled={isOptInButtonDisabled}
          playersForOptOut={playersForOptOut}
          playersForRemove={playersForRemove}
          playersForWait={playersForWait}
          playersForCheckIn={playersForCheckIn}
          setPlayersForCheckIn={setPlayersForCheckIn}
          duprPromptEnabled={headerContent.details.duprPromptEnabled}
          numberOfPlayers={players.length}
          setUncheckedPlayers={setUncheckedPlayers}
          uncheckedPlayers={uncheckedPlayers}
          chargeOptIn={chargeOptIn}
          chargeOptInURL={chargeOptInURL}
          slug={slug}
        />
      )
    )}
  </div>
);
